import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import { useToast } from '../hooks/useToast';
import { apiClient } from '../utils/apiClient';

const AuctionRoomManagement = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { hasPermission } = usePermissions();
  const toast = useToast();
  const [auctionRooms, setAuctionRooms] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyRooms, setHistoryRooms] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [historyStatusFilter, setHistoryStatusFilter] = useState('closed');
  const [selectedHistoryRoomId, setSelectedHistoryRoomId] = useState(null);
  const [historyDetailCache, setHistoryDetailCache] = useState({});
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [formData, setFormData] = useState({
    productId: '',
    participationFee: '',
    minBid: '',
    bidIncrement: '',
  increment: 3, // derived from pack config (legacy UI; server overrides)
    startTime: '',
    durationMinutes: '', // admin enters how long the auction lasts instead of explicit end time
    mode: 'fixed', // 'fixed' or 'auto-extend'
    autoExtendDuration: 10, // seconds to extend when triggered
    autoExtendTriggerSeconds: 60, // trigger threshold (last X seconds)
    status: 'pending'
  });

  const selectedProduct = useMemo(
    () => products.find(product => product?._id === formData.productId) || null,
    [products, formData.productId]
  );

  const participationFeeFromSelection = useMemo(() => {
    if (selectedProduct?.participationFee !== undefined && selectedProduct?.participationFee !== null) {
      const numeric = Number(selectedProduct.participationFee);
      return Number.isFinite(numeric) ? numeric : null;
    }

    if (formData.participationFee !== '' && formData.participationFee !== null) {
      const numeric = Number(formData.participationFee);
      return Number.isFinite(numeric) ? numeric : null;
    }

    return null;
  }, [selectedProduct, formData.participationFee]);

  useEffect(() => {
    if (hasPermission('view_auctions')) {
      fetchAuctionRooms();
      fetchProducts();
      fetchHistoryRooms(historyStatusFilter);
    }
  }, [hasPermission, historyStatusFilter]);

  const fetchAuctionRooms = async () => {
    try {
      const data = await apiClient.get('/api/auction-rooms');
      if (data.success) {
        setAuctionRooms(data.data);
      }
    } catch (error) {
      console.error('Error fetching auction rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const data = await apiClient.get('/api/products');
      if (data.success) {
        // Support both {data: {products: []}} and {data: []}
        const list = Array.isArray(data.data) ? data.data : (data.data?.products || []);
        setProducts(list);
      } else if (Array.isArray(data.products)) {
        setProducts(data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchHistoryRooms = async (status) => {
    setHistoryLoading(true);
    setHistoryError(null);

    try {
      const data = await apiClient.get('/api/auction-rooms', { params: { status, limit: '30' } });
      if (data.success) {
        const rooms = data.data || [];
        setHistoryRooms(rooms);
        if (rooms.length && !selectedHistoryRoomId) {
          setSelectedHistoryRoomId(rooms[0]._id);
        } else if (rooms.length && !rooms.find(room => room._id === selectedHistoryRoomId)) {
          setSelectedHistoryRoomId(rooms[0]._id);
        }
      } else {
        setHistoryError(data.message || 'Failed to load history');
      }
    } catch (error) {
      console.error('Error fetching auction room history:', error);
      setHistoryError('Unable to load auction history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchHistoryRoomDetails = async (roomId) => {
    if (!roomId || historyDetailCache[roomId]) return;
    setHistoryDetailLoading(true);
    try {
      const data = await apiClient.get(`/api/auction-rooms/${roomId}`);
      if (data.success) {
        setHistoryDetailCache(prev => ({
          ...prev,
          [roomId]: data.data
        }));
      }
    } catch (error) {
      console.error('Error fetching history room details:', error);
    } finally {
      setHistoryDetailLoading(false);
    }
  };

  useEffect(() => {
    if (selectedHistoryRoomId) {
      fetchHistoryRoomDetails(selectedHistoryRoomId);
    }
  }, [selectedHistoryRoomId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'productId') {
      const selectedProduct = products.find(product => product?._id === value);
      const derivedFee = selectedProduct?.participationFee;
      setFormData(prev => ({
        ...prev,
        productId: value,
        participationFee: derivedFee !== undefined && derivedFee !== null ? String(derivedFee) : ''
      }));
      return;
    }

    if (name === 'participationFee') {
      setFormData(prev => ({
        ...prev,
        participationFee: value
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Compute end time from duration
    if (!formData.startTime || !formData.durationMinutes) {
      toast.warning(t('auctionManagement.errors.startTimeRequired'));
      return;
    }
    const startDate = new Date(formData.startTime);
    const durationMs = parseInt(formData.durationMinutes, 10) * 60000;
    if (isNaN(durationMs) || durationMs <= 0) {
      toast.warning(t('auctionManagement.errors.durationPositive'));
      return;
    }
    const computedEnd = new Date(startDate.getTime() + durationMs);
    if (startDate >= computedEnd) {
      toast.warning(t('auctionManagement.errors.endAfterStart'));
      return;
    }

    try {
      const effectiveParticipationFee = Math.max(0, participationFeeFromSelection ?? 0);
      
      // Build payload without frontend-only fields
      const payload = {
        productId: formData.productId,
        minBid: parseFloat(formData.minBid),
        bidIncrement: parseFloat(formData.bidIncrement),
        increment: parseInt(formData.increment, 10) || 3,
        startTime: startDate.toISOString(),
        endTime: computedEnd.toISOString(),
        mode: formData.mode,
        status: formData.status,
        participationFee: effectiveParticipationFee
      };

      if (formData.mode === 'auto-extend') {
        payload.autoExtend = {
          duration: parseInt(formData.autoExtendDuration, 10) || 10,
            // how many seconds to add when extension triggers
          triggerSeconds: parseInt(formData.autoExtendTriggerSeconds, 10) || 60
            // when remaining time <= triggerSeconds, extend
        };
      }

      const data = editingRoom
        ? await apiClient.put(`/api/auction-rooms/${editingRoom._id}`, payload)
        : await apiClient.post('/api/auction-rooms', payload);

      // data is already parsed JSON
      
      if (data.success) {
        await fetchAuctionRooms();
        resetForm();
        setShowModal(false);
      } else {
        toast.error(data.message || t('auctionManagement.errors.saveFailed'));
      }
    } catch (error) {
      console.error('Error saving auction room:', error);
      toast.error(t('auctionManagement.errors.saveFailed'));
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    const start = room.startTime ? new Date(room.startTime) : null;
    const end = room.endTime ? new Date(room.endTime) : null;
    const durationMinutes = (start && end) ? Math.round((end - start) / 60000) : '';
    setFormData({
      productId: room.productId?._id || '',
      participationFee: room.participationFee !== undefined && room.participationFee !== null
        ? String(room.participationFee)
        : room.productId?.participationFee !== undefined && room.productId?.participationFee !== null
          ? String(room.productId.participationFee)
          : '',
      minBid: room.minBid || '',
      bidIncrement: room.bidIncrement || '',
  increment: room.increment || 3,
      startTime: start ? start.toISOString().slice(0, 16) : '',
      durationMinutes,
      mode: room.mode || 'fixed',
      autoExtendDuration: room.autoExtend?.duration || 10,
      autoExtendTriggerSeconds: room.autoExtend?.triggerSeconds || 60,
      status: room.status || 'pending'
    });
    setShowModal(true);
  };

  const handleDelete = async (roomId) => {
    // TODO: Replace window.confirm with a proper confirmation dialog/toast
    if (!window.confirm(t('auctionManagement.errors.deleteConfirm'))) return;

    try {
      const data = await apiClient.delete(`/api/auction-rooms/${roomId}`);
      
      if (data.success) {
        await fetchAuctionRooms();
      } else {
        toast.error(data.message || t('auctionManagement.errors.deleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting auction room:', error);
      toast.error(t('auctionManagement.errors.deleteFailed'));
    }
  };

  const resetForm = () => {
    setFormData({
      productId: '',
      participationFee: '',
      minBid: '',
      bidIncrement: '',
  increment: 3,
      startTime: '',
      durationMinutes: '',
      mode: 'fixed',
      autoExtendDuration: 10,
      autoExtendTriggerSeconds: 60,
      status: 'pending'
    });
    setEditingRoom(null);
  };

  const formatPrice = (price) => `${new Intl.NumberFormat('en-US').format(price ?? 0)} TND`;

  const formatTokens = (amount) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric) || numeric < 0) {
      return '0';
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numeric);
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '—';
    const parsed = new Date(dateTime);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-600';
      case 'pending': return 'bg-yellow-600';
      case 'closed': return 'bg-gray-600';
      case 'cancelled': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  const formatUserName = (user) => {
    if (!user) return t('auctionManagement.unknownUser');
    const first = user.profile?.firstName || '';
    const last = user.profile?.lastName || '';
    const fullName = `${first} ${last}`.trim();
    return fullName || user.username || t('auctionManagement.unknownUser');
  };

  const historyFilterOptions = [
    { label: t('auctionManagement.history.closed'), value: 'closed' },
    { label: t('auctionManagement.history.cancelled'), value: 'cancelled' }
  ];

  const selectedHistorySummary = useMemo(
    () => historyRooms.find(room => room._id === selectedHistoryRoomId) || null,
    [historyRooms, selectedHistoryRoomId]
  );

  const selectedHistoryDetails = selectedHistoryRoomId
    ? historyDetailCache[selectedHistoryRoomId]
    : null;

  const selectedHistoryRoom = selectedHistoryDetails || selectedHistorySummary;

  const sortedHistoryBids = useMemo(() => {
    if (!selectedHistoryRoom?.bids) return [];
    return [...selectedHistoryRoom.bids].sort((a, b) => {
      const bTime = new Date(b.timestamp || b.createdAt || b.time || 0).getTime() || 0;
      const aTime = new Date(a.timestamp || a.createdAt || a.time || 0).getTime() || 0;
      return bTime - aTime;
    });
  }, [selectedHistoryRoom]);

  if (!hasPermission('view_auctions')) {
    return (
      <SciFiBackground>
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">{t('auctionManagement.accessDenied')}</h1>
            <p className="text-gray-600 dark:text-gray-300">{t('auctionManagement.noPermission')}</p>
          </div>
        </div>
      </SciFiBackground>
    );
  }

  return (
    <SciFiBackground>
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold">{t('auctionManagement.title')}</h1>
          {hasPermission('create_auction') && (
            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              {t('auctionManagement.createRoom')}
            </button>
          )}
        </div>

        {/* Auction Rooms Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900 dark:border-white"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {auctionRooms.map((room) => (
              <div key={room._id} className="bg-white/80 dark:bg-gray-800/50 rounded-lg p-6 shadow-sm dark:shadow-none">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold mb-2">{room.productId?.name || t('auctionManagement.unknownProduct')}</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">{room.productId?.category}</p>
                  </div>
                  <span className={`px-3 py-1 rounded text-sm ${getStatusColor(room.status)}`}>
                    {room.status}
                  </span>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.minBid')}</span>
                    <span className="text-green-600 dark:text-green-400 font-bold">{formatPrice(room.minBid)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.bidIncrement')}</span>
                    <span className="text-blue-600 dark:text-blue-400">{formatPrice(room.bidIncrement)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.currentBid')}</span>
                    <span className="text-yellow-600 dark:text-yellow-400 font-bold">
                      {room.currentHighestBid ? formatPrice(room.currentHighestBid.amount) : t('auctionManagement.noBid')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.participants')}</span>
                    <span className="text-purple-600 dark:text-purple-400">{room.analytics?.participantCount || 0}</span>
                  </div>
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.start')}</span>
                    <span className="ml-2">{formatDateTime(room.startTime)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.end')}</span>
                    <span className="ml-2">{formatDateTime(room.endTime)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.mode')}</span>
                    <span className="ml-2 capitalize">{room.mode}</span>
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  {hasPermission('manage_auctions') && room.status !== 'active' && (
                    <button
                      onClick={() => handleEdit(room)}
                      className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded text-sm transition-colors"
                    >
                      {t('edit')}
                    </button>
                  )}
                  {hasPermission('delete_auction') && room.status !== 'active' && (
                    <button
                      onClick={() => handleDelete(room._id)}
                      className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded text-sm transition-colors"
                    >
                      {t('delete')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold">{t('auctionManagement.history.title')}</h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm">{t('auctionManagement.history.subtitle')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {historyFilterOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSelectedHistoryRoomId(null);
                    setHistoryStatusFilter(option.value);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${historyStatusFilter === option.value ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700/80 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <div className="bg-white/80 dark:bg-gray-800/50 rounded-lg p-4 shadow-sm dark:shadow-none">
              <h3 className="text-xl font-semibold mb-4">{t('auctionManagement.history.recentRooms')}</h3>
              {historyLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 dark:border-white"></div>
                </div>
              ) : historyError ? (
                <p className="text-red-600 dark:text-red-300 text-sm">{historyError}</p>
              ) : historyRooms.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm">{t('auctionManagement.history.noRooms')}</p>
              ) : (
                <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                  {historyRooms.map(room => {
                    const isSelected = room._id === selectedHistoryRoomId;
                    return (
                      <button
                        key={room._id}
                        onClick={() => setSelectedHistoryRoomId(room._id)}
                        className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${isSelected ? 'bg-blue-100 dark:bg-blue-600/20 border-blue-500' : 'bg-gray-100 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700 hover:border-blue-500/60'}`}
                      >
                        <p className="font-semibold text-gray-900 dark:text-white">{room.productId?.name || t('auctionManagement.unknownProduct')}</p>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>{formatDateTime(room.endTime)}</span>
                          <span>{room.analytics?.participantCount || room.participants?.length || 0} {t('auctionManagement.history.participants')}</span>
                        </div>
                        <div className="flex justify-between items-center mt-2 text-sm">
                          <span className="text-gray-600 dark:text-gray-300">{t('auctionManagement.history.finalBid')}</span>
                          <span className="font-semibold text-yellow-600 dark:text-yellow-300">{room.currentHighestBid?.amount ? formatPrice(room.currentHighestBid.amount) : '—'}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="lg:col-span-2 bg-white/80 dark:bg-gray-800/50 rounded-lg p-6 min-h-[420px] shadow-sm dark:shadow-none">
              {selectedHistoryRoom ? (
                <>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                      <h3 className="text-2xl font-semibold">{selectedHistoryRoom.productId?.name || t('auctionManagement.unknownProduct')}</h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">{selectedHistoryRoom.productId?.category || t('auctionManagement.history.noCategory')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded text-sm capitalize ${getStatusColor(selectedHistoryRoom.status)}`}>
                        {selectedHistoryRoom.status}
                      </span>
                      {selectedHistoryRoom.currentHighestBid?.amount && (
                        <div className="text-right">
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('auctionManagement.history.finalPrice')}</p>
                          <p className="text-lg font-bold text-yellow-600 dark:text-yellow-300">{formatPrice(selectedHistoryRoom.currentHighestBid.amount)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 text-sm">
                    <div className="bg-gray-100 dark:bg-gray-900/40 rounded-lg p-4 space-y-2">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">{t('auctionManagement.history.startTime')}</p>
                        <p className="font-semibold">{formatDateTime(selectedHistoryRoom.startTime)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">{t('auctionManagement.history.endTime')}</p>
                        <p className="font-semibold">{formatDateTime(selectedHistoryRoom.endTime)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">{t('auctionManagement.history.duration')}</p>
                        <p className="font-semibold">
                          {selectedHistoryRoom.startTime && selectedHistoryRoom.endTime
                            ? `${Math.max(1, Math.round((new Date(selectedHistoryRoom.endTime) - new Date(selectedHistoryRoom.startTime)) / 60000))} min`
                            : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-900/40 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('common.participants')}</span>
                        <span className="font-semibold">{selectedHistoryRoom.analytics?.participantCount ?? selectedHistoryRoom.participants?.length ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.history.winner')}</span>
                        <span className="font-semibold text-green-600 dark:text-green-300">
                          {selectedHistoryRoom.currentHighestBid?.userId
                            ? formatUserName(selectedHistoryRoom.currentHighestBid.userId)
                            : t('auctionManagement.history.noWinner')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.history.participationFee')}</span>
                        <span className="font-semibold">{formatPrice(selectedHistoryRoom.participationFee || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('auctionManagement.history.minBidIncrement')}</span>
                        <span className="font-semibold">{formatPrice(selectedHistoryRoom.minBid)} / {formatPrice(selectedHistoryRoom.bidIncrement)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xl font-semibold">{t('auctionManagement.history.bidHistory')}</h4>
                      {historyDetailLoading && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">{t('auctionManagement.history.refreshing')}</span>
                      )}
                    </div>
                    {sortedHistoryBids.length > 0 ? (
                      <div className="mt-4 overflow-x-auto max-h-80 pr-1">
                        <table className="w-full text-left text-sm">
                          <thead className="text-gray-500 dark:text-gray-400 uppercase text-xs">
                            <tr>
                              <th className="py-2 pr-4">{t('auctionManagement.history.bidder')}</th>
                              <th className="py-2 pr-4">{t('auctionManagement.history.amount')}</th>
                              <th className="py-2">{t('auctionManagement.history.time')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedHistoryBids.map((bid, index) => (
                              <tr key={`${bid._id || bid.bidId || index}`} className="border-t border-gray-200 dark:border-gray-700/60">
                                <td className="py-2 pr-4 text-gray-900 dark:text-white">
                                  {formatUserName(bid.userId)}
                                  {bid.isAuto && <span className="ml-2 text-xs text-blue-600 dark:text-blue-300">{t('auctionManagement.history.auto')}</span>}
                                </td>
                                <td className="py-2 pr-4 text-yellow-600 dark:text-yellow-200 font-semibold">{formatPrice(bid.amount || 0)}</td>
                                <td className="py-2 text-gray-600 dark:text-gray-300">{formatDateTime(bid.timestamp || bid.createdAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">{t('auctionManagement.history.noBids')}</p>
                    )}
                  </div>
                </>
              ) : historyLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 dark:border-white"></div>
                </div>
              ) : (
                <div className="text-gray-500 dark:text-gray-400 text-center py-12">{t('auctionManagement.history.selectRoom')}</div>
              )}
            </div>
          </div>
        </div>

        {/* Auction Room Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6">
                {editingRoom ? t('auctionManagement.form.editTitle') : t('auctionManagement.form.createTitle')}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.product')}</label>
                  <select
                    name="productId"
                    value={formData.productId}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="">{t('auctionManagement.form.selectProduct')}</option>
                        {products
                      .filter(p => p?.isReservable === true)
                      .map(product => (
                        <option key={product._id} value={product._id}>
                          {product.name} - {formatPrice(product.price)} • Fee: {formatPrice(product.participationFee ?? 0)}
                        </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('auctionManagement.form.reservableOnly')}</p>
                  {participationFeeFromSelection !== null && (
                    <div className="mt-3 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2">
                      <p className="text-sm font-semibold text-blue-700 dark:text-blue-100">
                        {t('auctionManagement.form.feeApplied', { fee: formatPrice(participationFeeFromSelection) })}
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-200 mt-1">
                        {t('auctionManagement.form.feeFromProduct')}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.participationFee')}</label>
                  <input
                    type="number"
                    name="participationFee"
                    value={participationFeeFromSelection ?? ''}
                    readOnly
                    min="0"
                    step="0.01"
                    className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 read-only:opacity-80"
                    placeholder={formData.productId ? '0' : t('auctionManagement.form.selectProductFirst')}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('auctionManagement.form.feeHelp')}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.minimumBid')}</label>
                    <input
                      type="number"
                      name="minBid"
                      value={formData.minBid}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                      className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.bidIncrement')}</label>
                    <input
                      type="number"
                      name="bidIncrement"
                      value={formData.bidIncrement}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0.01"
                      required
                      className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.startTime')}</label>
                    <input
                      type="datetime-local"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.durationMinutes')}</label>
                    <input
                      type="number"
                      name="durationMinutes"
                      value={formData.durationMinutes}
                      onChange={handleInputChange}
                      min="1"
                      required
                      className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                {formData.startTime && formData.durationMinutes && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t('auctionManagement.form.computedEndTime')}{' '}
                    {new Date(new Date(formData.startTime).getTime() + (parseInt(formData.durationMinutes,10)||0)*60000).toLocaleString()}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.packIncrement')}</label>
                    <div className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white text-sm flex justify-between">
                      <span>{formData.increment}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t('auctionManagement.form.derivedServerSide')}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.mode')}</label>
                    <select
                      name="mode"
                      value={formData.mode}
                      onChange={handleInputChange}
                      className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="fixed">{t('auctionManagement.form.fixed')}</option>
                      <option value="auto-extend">{t('auctionManagement.form.autoExtend')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.status')}</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="pending">{t('auctionManagement.form.pending')}</option>
                      <option value="active">{t('auctionManagement.form.active')}</option>
                      <option value="closed">{t('auctionManagement.form.closed')}</option>
                      <option value="cancelled">{t('auctionManagement.form.cancelled')}</option>
                    </select>
                  </div>
                </div>

                {formData.mode === 'auto-extend' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.autoExtendDuration')}</label>
                      <input
                        type="number"
                        name="autoExtendDuration"
                        value={formData.autoExtendDuration}
                        onChange={handleInputChange}
                        min="1"
                        className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">{t('auctionManagement.form.triggerThreshold')}</label>
                      <input
                        type="number"
                        name="autoExtendTriggerSeconds"
                        value={formData.autoExtendTriggerSeconds}
                        onChange={handleInputChange}
                        min="1"
                        className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-6 py-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-700 dark:text-white rounded-lg transition-colors"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    {editingRoom ? t('auctionManagement.form.update') : t('auctionManagement.form.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </SciFiBackground>
  );
};

export default AuctionRoomManagement;
